import { useCallback, useEffect, useRef, useState } from "react";
import { apiErrorMessage, type Me, type RaceApi } from "../api";

export type SessionStatus = "loading" | "signed-in" | "signed-out" | "error";

export interface SessionController {
  status: SessionStatus;
  me: Me | null;
  error: string | null;
  testLoginAvailable: boolean;
  refresh(signal?: AbortSignal): Promise<Me | null>;
  applyMe(me: Me | null): void;
  signOut(): Promise<void>;
}

/**
 * Loads `/api/me` once and exposes a refresh hook for sign-in, profile save,
 * and sign-out. A 401 is a normal signed-out state, not an error.
 */
export function useSession(api: RaceApi): SessionController {
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testLoginAvailable, setTestLoginAvailable] = useState(false);
  const mounted = useRef(true);

  const refresh = useCallback(
    async (signal?: AbortSignal): Promise<Me | null> => {
      try {
        const next = await api.getMe(signal);
        if (signal?.aborted || !mounted.current) return next;
        setMe(next);
        setStatus(next ? "signed-in" : "signed-out");
        setError(null);
        return next;
      } catch (cause) {
        if (signal?.aborted || !mounted.current) return null;
        setError(apiErrorMessage(cause));
        setStatus("error");
        return null;
      }
    },
    [api],
  );

  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController();
    void refresh(controller.signal);
    void api
      .isTestLoginAvailable()
      .then((available) => {
        if (mounted.current) setTestLoginAvailable(available);
      })
      .catch(() => {
        if (mounted.current) setTestLoginAvailable(false);
      });
    return () => {
      mounted.current = false;
      controller.abort();
    };
  }, [api, refresh]);

  const signOut = useCallback(async (): Promise<void> => {
    await api.signOut();
    setMe(null);
    setStatus("signed-out");
    setError(null);
  }, [api]);

  const applyMe = useCallback((next: Me | null): void => {
    setMe(next);
    setStatus(next ? "signed-in" : "signed-out");
    setError(null);
  }, []);

  return { status, me, error, testLoginAvailable, refresh, applyMe, signOut };
}
